import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, BarChart2, Edit2, Trash2, RefreshCw, Megaphone } from 'lucide-react';
import {
  fetchFeedbackCampaigns,
  deleteFeedbackCampaign,
  FeedbackCampaign,
} from '../services/api.ts';
import './FeedbackCampaigns.css';

const STATUS_FILTERS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const FeedbackCampaigns: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<FeedbackCampaign[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await fetchFeedbackCampaigns(statusFilter || undefined));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id: number) => {
    try {
      await deleteFeedbackCampaign(id);
      setDeleteId(null);
      load();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Delete failed');
    }
  };

  const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '—');
  const progressPct = (c: FeedbackCampaign) =>
    c.target_sample_size > 0
      ? Math.min(100, Math.round(((c.shown_count ?? 0) / c.target_sample_size) * 100))
      : 0;

  return (
    <div className="feedback-campaigns-page">
      <div className="page-header">
        <h1><Megaphone size={22} /> Feedback Campaigns</h1>
        <div className="page-header-actions">
          <button className="btn-refresh" onClick={load} title="Refresh">
            <RefreshCw size={16} />
          </button>
          <Link to="/dashboard/feedback-campaigns/new" className="btn-primary">
            <Plus size={16} /> New Campaign
          </Link>
        </div>
      </div>

      <div className="filter-bar">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value || 'all'}
            className={`filter-chip ${statusFilter === f.value ? 'active' : ''}`}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading ? (
        <div className="loading-spinner">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">No campaigns yet. Create your first one.</div>
      ) : (
        <table className="campaigns-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Status</th>
              <th>Country</th>
              <th>Platform</th>
              <th>Progress</th>
              <th>Submitted</th>
              <th>Window</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  <Link to={`/dashboard/feedback-campaigns/edit/${c.id}`} className="row-title">
                    {c.title}
                  </Link>
                  {c.description && <div className="row-subtitle">{c.description}</div>}
                </td>
                <td><span className={`status-pill status-${c.status}`}>{c.status}</span></td>
                <td>{c.country_id ?? 'All'}</td>
                <td>{c.platform}</td>
                <td>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progressPct(c)}%` }} />
                  </div>
                  <div className="progress-text">
                    {c.shown_count ?? 0} / {c.target_sample_size}
                  </div>
                </td>
                <td>{c.submitted_count ?? 0}</td>
                <td className="window-cell">
                  <div>{fmt(c.starts_at)}</div>
                  <div className="muted">→ {fmt(c.ends_at)}</div>
                </td>
                <td className="actions-cell">
                  <button
                    className="icon-btn"
                    title="Analytics"
                    onClick={() => navigate(`/dashboard/feedback-campaigns/${c.id}/analytics`)}
                  >
                    <BarChart2 size={16} />
                  </button>
                  <button
                    className="icon-btn"
                    title="Edit"
                    onClick={() => navigate(`/dashboard/feedback-campaigns/edit/${c.id}`)}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    className="icon-btn danger"
                    title="Delete"
                    onClick={() => setDeleteId(c.id)}
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {deleteId !== null && (
        <div className="modal-backdrop" onClick={() => setDeleteId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete campaign?</h3>
            <p>This permanently removes the campaign, its questions, and all collected responses.</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => handleDelete(deleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackCampaigns;
